import { useState, useEffect } from 'react';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

/**
 * Hook pour récupérer et calculer les heures de formation des apprentis
 * @param {Date} currentMonth - Mois en cours pour calculer les heures
 * @returns {Object} schoolData - Données des formations par opérateur
 */
export function useSchoolHours(currentMonth) {
  const [schoolData, setSchoolData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSchoolHours = async () => {
      try {
        setLoading(true);
        
        // Récupérer toutes les configurations école
        const response = await fetch(API_ENDPOINTS.SCHOOL_LIST(), {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include'
        });

        if (!response.ok) {
          console.error('Erreur lors de la récupération des configurations école');
          return;
        }

        const schools = await response.json();
        console.log('🏫 School configurations:', schools);

        // Traiter chaque configuration
        const processedData = {};
        const today = new Date();
        const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const currentMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

        for (const school of schools) {
          const { operator_id, employee_id, weekly_hours, school_days, contract_end_date } = school;

          // Vérifier si le contrat est expiré
          if (contract_end_date && new Date(contract_end_date) < today) {
            console.log(`🏫 Contrat expiré pour operator ${operator_id}, suppression...`);
            // Supprimer la configuration école
            await deleteSchoolConfig(operator_id);
            continue;
          }

          // Calculer les heures par jour d'école
          const hoursPerDay = school_days.length > 0 ? weekly_hours / school_days.length : 0;

          // Calculer les heures accumulées pour le mois
          const accumulatedHours = calculateAccumulatedHours(
            school_days,
            hoursPerDay,
            currentMonthStart,
            currentMonthEnd,
            today
          );

          processedData[operator_id] = {
            employeeId: employee_id,
            weeklyHours: weekly_hours,
            schoolDays: school_days,
            hoursPerDay: hoursPerDay,
            accumulatedHours: accumulatedHours,
            contractEndDate: contract_end_date
          };
        }

        setSchoolData(processedData);
      } catch (error) {
        console.error('Erreur useSchoolHours:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentMonth) {
      fetchSchoolHours();
    }
  }, [currentMonth]);

  return { schoolData, loading };
}

/**
 * Calculer les heures accumulées en fonction des jours d'école passés
 */
function calculateAccumulatedHours(schoolDays, hoursPerDay, monthStart, monthEnd, today) {
  let totalHours = 0;
  
  // Mapper les jours français vers les jours JavaScript (0 = Dimanche)
  const dayMap = {
    'DIMANCHE': 0,
    'LUNDI': 1,
    'MARDI': 2,
    'MERCREDI': 3,
    'JEUDI': 4,
    'VENDREDI': 5,
    'SAMEDI': 6
  };

  // Convertir les jours d'école en numéros
  const schoolDayNumbers = schoolDays.map(day => dayMap[day]).filter(d => d !== undefined);

  // Parcourir chaque jour du mois jusqu'à aujourd'hui
  const endDate = today < monthEnd ? today : monthEnd;
  
  for (let date = new Date(monthStart); date <= endDate; date.setDate(date.getDate() + 1)) {
    const dayOfWeek = date.getDay();
    
    // Si c'est un jour d'école et que ce jour est passé
    if (schoolDayNumbers.includes(dayOfWeek) && date < today) {
      totalHours += hoursPerDay;
    }
  }

  return Math.round(totalHours * 10) / 10; // Arrondir à 1 décimale
}

/**
 * Supprimer une configuration école expirée
 */
async function deleteSchoolConfig(operatorId) {
  try {
    const response = await fetch(API_ENDPOINTS.SCHOOL_DETAIL(operatorId), {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });

    if (response.ok) {
      console.log(`✅ Configuration école supprimée pour operator ${operatorId}`);
    }
  } catch (error) {
    console.error(`❌ Erreur suppression config école operator ${operatorId}:`, error);
  }
}
